import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { getResumenEjecutivos } from '../api/apiConfig';
import { MdReceipt, MdArrowForward } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import * as XLSX from 'xlsx';
import { formatMoneyMM } from '../utils/numberFormat';

const CarteraEjecutivo = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [ejecutivosData, setEjecutivosData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  const formatNumber = (num) => formatMoneyMM(num);

  const filterData = (dataToFilter) => {
    if (!searchTerm.trim()) return dataToFilter;
    const searchLower = searchTerm.toLowerCase().trim();
    return dataToFilter.filter(item => {
      const searchableFields = [item.NOMBRE, item.GRUPO, item.CLIENTES_ACTIVOS, item.CLIENTES_ACTIVOS_6M, item.CLIENTES_CON_CLAVE, item.DEUDA_NETA, item.VENC_DEUDA_NETA, item.PROYECCION, item.META_FACTOTRING, item.MONTO_ANTICIPADO, item.PROGRESO, item.PENETRACION_IF];
      return searchableFields.some(field => field != null && String(field).toLowerCase().includes(searchLower));
    });
  };

  const getSortedData = () => {
    let dataToSort = filterData(ejecutivosData);
    if (!sortColumn) return dataToSort;
    return [...dataToSort].sort((a, b) => {
      let aValue = a[sortColumn]; let bValue = b[sortColumn];
      if (aValue == null) aValue = ''; if (bValue == null) bValue = '';
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);
      const comparison = isNumeric ? aNum - bNum : String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase(), 'es', { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const sortedData = getSortedData();

  const handleSort = (column) => {
    if (sortColumn === column) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDirection('asc'); }
  };

  const handleSearchChange = (e) => { setSearchTerm(e.target.value); };
  const clearSearch = () => { setSearchTerm(''); };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort style={{ marginLeft: '4px', opacity: 0.3 }} />;
    return sortDirection === 'asc' ? <FaSortUp style={{ marginLeft: '4px' }} /> : <FaSortDown style={{ marginLeft: '4px' }} />;
  };

  const exportToExcel = () => {
    const dataToExport = getSortedData();
    const data = dataToExport.map(item => ({
      'Ejecutivo': item.NOMBRE || '',
      'Equipo': item.GRUPO || '',
      'Clientes en Cartera': item.CLIENTES_ACTIVOS || 0,
      'Clientes con operación últ. 6 meses': item.CLIENTES_ACTIVOS_6M || 0,
      'Clientes con clave SII': item.CLIENTES_CON_CLAVE || 0,
      'Stock (MM$)': parseFloat(item.DEUDA_NETA || 0).toFixed(2).replace('.', ','),
      'Venc. Stock (MM$)': parseFloat(item.VENC_DEUDA_NETA || 0).toFixed(2).replace('.', ','),
      'Proyección (MM$)': parseFloat(item.PROYECCION || 0).toFixed(2).replace('.', ','),
      'Meta Factoring (MM$)': parseFloat(item.META_FACTOTRING || 0).toFixed(2).replace('.', ','),
      'Volumen Factoring (MM$)': parseFloat(item.MONTO_ANTICIPADO || 0).toFixed(2).replace('.', ','),
      'Progreso Factoring (%)': ((item.PROGRESO || 0) * 100).toFixed(1).replace('.', ','),
      'Penetración IF (%)': ((item.PENETRACION_IF ?? 0) * 100).toFixed(1).replace('.', ',')
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Resumen Ejecutivos');
    
    XLSX.writeFile(wb, `resumen_ejecutivos_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  useEffect(() => {
    const fetchEjecutivos = async () => {
      if (!user) return;
      
      try {
        const data = await getResumenEjecutivos(user);
        setEjecutivosData(data || []);
      } catch (error) {
        console.error('Error fetching ejecutivos:', error);
        setEjecutivosData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEjecutivos();
  }, [user]);

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Cartera</h1>
        <p className="text-gray-600">Resumen por ejecutivos</p>
      </div>

      <div className="seccion-datos">
        <div className="flex justify-between items-center mb-4">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600'}}>
            Resumen por Ejecutivo
            <span className="text-sm ml-2" style={{color: 'var(--if-text-muted)'}}>
              ({ejecutivosData.length} registros)
            </span>
          </h3>
          {ejecutivosData.length > 0 && (
            <button
              onClick={exportToExcel}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                borderLeft: '3px solid var(--if-primary)'
              }}
            >
              <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
              Exportar Excel
            </button>
          )}
        </div>
        {ejecutivosData.length > 0 && (
          <div className="mb-4">
            <div className="relative" style={{ maxWidth: '500px' }}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IoSearchOutline className="h-5 w-5" style={{ color: '#9ca3af' }} />
              </div>
              <input type="text" value={searchTerm} onChange={handleSearchChange} placeholder="Buscar en todas las columnas..." className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              {searchTerm && <button type="button" onClick={clearSearch} className="absolute inset-y-0 right-0 pr-3 flex items-center" style={{ color: '#9ca3af' }} title="Limpiar búsqueda"><IoClose className="h-5 w-5" /></button>}
            </div>
            {searchTerm && <div className="mt-2 text-sm" style={{ color: 'var(--if-text-muted)' }}>{sortedData.length === 0 ? <span>No se encontraron resultados para &quot;{searchTerm}&quot;</span> : <span>{sortedData.length} resultado{sortedData.length !== 1 ? 's' : ''} encontrado{sortedData.length !== ejecutivosData.length ? ` de ${ejecutivosData.length} registros` : ''}</span>}</div>}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
          </div>
        ) : ejecutivosData.length === 0 ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>
            No hay datos disponibles
          </div>
        ) : sortedData.length === 0 && searchTerm ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>No se encontraron resultados para &quot;{searchTerm}&quot;</div>
        ) : (
          <div className="tabla-container" style={{overflowX: 'auto', maxHeight: '600px'}}>
            <table>
              <thead>
                <tr>
                  <th style={{position: 'sticky', left: 0, zIndex: 11, background: 'linear-gradient(135deg, var(--if-primary) 0%, var(--if-primary-dark) 100%)', minWidth: '200px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('NOMBRE')}>Ejecutivo {getSortIcon('NOMBRE')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('GRUPO')}>Equipo {getSortIcon('GRUPO')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('CLIENTES_ACTIVOS')}>Clientes en Cartera {getSortIcon('CLIENTES_ACTIVOS')}</th>
                  <th style={{minWidth: '160px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('CLIENTES_ACTIVOS_6M')}>Clientes con operación últ. 6 meses {getSortIcon('CLIENTES_ACTIVOS_6M')}</th>
                  <th style={{minWidth: '180px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('CLIENTES_CON_CLAVE')}>Clientes con operación últ. 6 meses y clave SII {getSortIcon('CLIENTES_CON_CLAVE')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('DEUDA_NETA')}>Stock<br/>(cierre mes<br/>anterior) {getSortIcon('DEUDA_NETA')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('VENC_DEUDA_NETA')}>Venc. Stock<br/>(cierre mes<br/>anterior) {getSortIcon('VENC_DEUDA_NETA')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('PROYECCION')}>Proyección {getSortIcon('PROYECCION')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('META_FACTOTRING')}>Meta Factoring {getSortIcon('META_FACTOTRING')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('MONTO_ANTICIPADO')}>Volumen Factoring {getSortIcon('MONTO_ANTICIPADO')}</th>
                  <th style={{minWidth: '160px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('PROGRESO')}>Progreso Factoring {getSortIcon('PROGRESO')}</th>
                  <th style={{minWidth: '140px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('PENETRACION_IF')}>Penetración IF {getSortIcon('PENETRACION_IF')}</th>
                  <th>Detalle Ejecutivo</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((item, index) => (
                  <tr key={index}>
                    <td style={{position: 'sticky', left: 0, zIndex: 10, backgroundColor: 'white', minWidth: '200px'}}>{item.NOMBRE}</td>
                    <td>{item.GRUPO}</td>
                    <td style={{textAlign: 'center'}}>{item.CLIENTES_ACTIVOS}</td>
                    <td style={{textAlign: 'center'}}>{item.CLIENTES_ACTIVOS_6M}</td>
                    <td style={{textAlign: 'center'}}>{item.CLIENTES_CON_CLAVE}</td>
                    <td style={{textAlign: 'right'}}>{formatNumber(item.DEUDA_NETA)}</td>
                    <td style={{textAlign: 'right'}}>{formatNumber(item.VENC_DEUDA_NETA)}</td>
                    <td style={{textAlign: 'right'}}>{formatNumber(item.PROYECCION)}</td>
                    <td style={{textAlign: 'right'}}>{formatNumber(item.META_FACTOTRING)}</td>
                    <td style={{textAlign: 'right'}}>{formatNumber(item.MONTO_ANTICIPADO)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-gray-600 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(item.PROGRESO * 100, 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-xs min-w-[40px]">{(item.PROGRESO * 100).toFixed(1)}%</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-gray-600 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${Math.min((item.PENETRACION_IF ?? 0) * 100, 100)}%`,
                              background: 'linear-gradient(90deg, #2563eb 0%, #1d4ed8 100%)'
                            }}
                          />
                        </div>
                        <span className="text-xs min-w-[40px]">{(((item.PENETRACION_IF ?? 0) * 100)).toFixed(1)}%</span>
                      </div>
                    </td>
                    <td style={{textAlign: 'center'}}>
                      <button
                        onClick={() => navigate(`/detalle-ejecutivo/${item.EJEC_SIST_FACTORING}`)}
                        className="btn-primary"
                        style={{
                          padding: '0.25rem 0.75rem',
                          fontSize: '0.75rem',
                          borderRadius: '4px',
                          width: '32px',
                          height: '32px'
                        }}
                        title="Ver detalle"
                      >
                        <MdArrowForward className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarteraEjecutivo;